import { Modal } from "bootstrap";

/**
 * Mixin de gestion des modales Bootstrap.
 *
 * Le composant hôte doit :
 *  - poser un `ref` sur l'élément `.modal` (nom par défaut : "contentModal",
 *    surchargeable en redéfinissant `modalRef` dans son `data()`) ;
 *  - déclencher l'ouverture / fermeture via `this.showModal()` / `this.hideModal()`
 *    depuis son propre `watch` (`opened`, `item`, `id`, store…) ou ses méthodes.
 *
 * Le mixin centralise tout le reste :
 *  - il réutilise l'instance Bootstrap via `Modal.getOrCreateInstance()` au lieu
 *    d'en recréer une à chaque appel — ce qui évitait des instances et des
 *    `.modal-backdrop` multiples ;
 *  - il écoute `hidden.bs.modal` et appelle `onModalHidden()` (émission de
 *    l'évènement `hidden` + retrait du listener `click`) ;
 *  - il détruit proprement l'instance au démontage (`dispose()`) et supprime un
 *    éventuel backdrop resté orphelin sur le `<body>`, y compris en production
 *    (ex. : modale retirée via `v-if` alors qu'elle est encore ouverte).
 *
 * Un composant peut surcharger `onModalHidden()` ou `stop()` pour ajouter sa
 * propre logique métier (restauration de données, synchronisation d'un store…).
 */
export const modalMixin = {
  data() {
    return {
      modalRef: "contentModal",
    };
  },
  mounted() {
    const el = this.getModalElement();
    if (el) {
      el.addEventListener("hidden.bs.modal", this.onModalHidden);
    }
  },
  beforeUnmount() {
    const el = this.getModalElement();
    if (el) {
      el.removeEventListener("hidden.bs.modal", this.onModalHidden);
      el.removeEventListener("click", this.stop);
      const instance = Modal.getInstance(el);
      if (instance) {
        // `_isTransitioning` est une propriété privée (préfixée `_`), non documentée dans l'API
        // publique de Bootstrap. Elle fonctionne avec Bootstrap 5.x mais n'est pas garantie stable :
        // à revérifier lors d'une montée de version de la lib.
        if (instance._isTransitioning) {
          // Un `hide()` est encore en cours (transition CSS non terminée) : disposer
          // maintenant viderait `instance._element` avant que le callback de fin de
          // transition de Bootstrap ne s'exécute, provoquant un crash (`_element is
          // null`) sur un composant démonté trop tôt (ex. : submit + fermeture
          // immédiate de la modale). On attend la fin réelle du hide pour disposer.
          el.addEventListener("hidden.bs.modal", () => instance.dispose(), { once: true });
        } else {
          instance.dispose();
        }
      }
    }
    // Garde-fou : si plus aucune modale n'est affichée, on s'assure que le
    // backdrop et le verrou de scroll du <body> ont bien été nettoyés.
    if (!document.querySelector(".modal.show")) {
      document.querySelectorAll(".modal-backdrop").forEach((node) => node.remove());
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
    }
  },
  methods: {
    /**
     * Retourne l'élément DOM `.modal` du composant (selon `modalRef`).
     * @returns {HTMLElement|null}
     */
    getModalElement() {
      return this.$refs[this.modalRef] || null;
    },

    /**
     * Affiche la modale en réutilisant l'instance Bootstrap existante.
     * @param {object} [options] Options Bootstrap (ex. : { backdrop: "static" }).
     * @returns {Modal|null}
     */
    showModal(options = {}) {
      const el = this.getModalElement();
      if (!el) {
        return null;
      }
      const instance = Modal.getOrCreateInstance(el, options);
      instance.show();
      return instance;
    },

    /**
     * Masque la modale si une instance existe.
     */
    hideModal() {
      const el = this.getModalElement();
      if (!el) {
        return;
      }
      const instance = Modal.getInstance(el);
      if (instance) {
        instance.hide();
      }
    },

    /**
     * Handler par défaut de l'évènement `hidden.bs.modal`.
     * Surchargeable par le composant pour y ajouter sa logique métier.
     */
    onModalHidden() {
      const el = this.getModalElement();
      if (el) {
        el.removeEventListener("click", this.stop);
      }
      this.$emit("hidden");
    },

    /**
     * Empêche la propagation d'un évènement (utilisé sur le clic du contenu).
     * @param {Event} e
     */
    stop(e) {
      e.stopPropagation();
    },
  },
};
